'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';

import { addEventLead } from '@publishing/hooks/useEventLeadGate';
import { CommonInner } from '@publishing/components/ui/CommonInner';
import { LEAD_FORM_NAME } from '@/features/lead-form/constants/formName';
import { StepForm } from '@publishing/components/forms';
import { useTwoStepLeadForm } from '@publishing/hooks/useTwoStepLeadForm';

import s from '../today.module.scss';
import EventBubble from '@/publishing/components/events/EventBubble';
import { pathWithQueryString } from '@publishing/helpers/pathWithQuery';
import { Title, TitleGroup } from '@publishing/components/ui/Title';
import Icon from '@publishing/components/ui/Icon';
import { StatusWarning } from '@publishing/components/ui/SvgSprite';
import { getPublishingImage } from '@/shared/utils/publishingAsset';
import { Picture } from '@publishing/components/ui/Picture';
import ContactTitleSvg from '@publishing/assets/icons/events/ff-today/contact_title.svg';
import IconFire1 from '@publishing/assets/icons/events/ff-today/icon_fire1.svg';
import IconFire2 from '@publishing/assets/icons/events/ff-today/icon_fire2.svg';
import IconThunder from '@publishing/assets/icons/events/ff-today/icon_thunder.svg';
import {
  TODAY_CONTACT_COPY,
  TODAY_CONTACT_OFFERS,
  TODAY_FIRE_DECOS,
  TODAY_SLUG,
  TODAY_SUBMIT_PATH,
  type TodayFireIcon,
} from '../data';
import { TodayCountdown, TodayPriceSlot, useTodaySlotPlayback } from '../todaySlot';

const FIRE_ICONS: Record<TodayFireIcon, typeof IconFire1> = {
  fire1: IconFire1,
  fire2: IconFire2,
};

/** 송도 → 마곡 → 강남 전환 간격. 슬롯이 시작된 시점부터 잰다. */
const CONTACT_OFFER_INTERVAL_MS = 7000;

/** 연락 라우트 전용: 제출 후 세션 기록 + 본페이지 이동 (게이트는 상위 layout) */
export default function TodayContactPage() {
  const form = useTwoStepLeadForm();
  const router = useRouter();
  const { countdown, spinId } = useTodaySlotPlayback('contact-offers', true, CONTACT_OFFER_INTERVAL_MS);
  const offer = TODAY_CONTACT_OFFERS[spinId % TODAY_CONTACT_OFFERS.length];

  const onLeadSuccess = useCallback(() => {
    addEventLead(TODAY_SLUG);
    router.replace(pathWithQueryString(TODAY_SUBMIT_PATH, window.location.search));
  }, [router]);

  return (
    <div className={`${s.contact} h_full`}>
      {TODAY_FIRE_DECOS.map(({ key, icon, className }) => {
        const FireIcon = FIRE_ICONS[icon];
        return (
          <span key={key} className={`${s.fire} ${s[className]}`} aria-hidden="true">
            <FireIcon />
          </span>
        );
      })}
      <CommonInner as="section" direction="row" className={s.inner}>
        <div className={s.contact_header}>
          <TitleGroup className={s.title_group} align="center">
            <EventBubble
              leftIcon={<Icon name={StatusWarning} theme="fill" size={16} color="var(--color-gray-900)" />}
              rightIcon={<Icon name={StatusWarning} theme="fill" size={16} color="var(--color-gray-900)" />}
              textSize={14}
              offset="center"
              radius="min"
              bgColor="#fff"
              className={s.badge}
            >
              {TODAY_CONTACT_COPY.badge}
            </EventBubble>
            <Title className={s.title} align="center">
              <ContactTitleSvg
                className={s.contact_title}
                style={{ color: 'var(--color-gray-100)' }}
                role="img"
                aria-label={TODAY_CONTACT_COPY.titleHidden}
              />
            </Title>
          </TitleGroup>
          <div className={s.slot_machine} aria-label={TODAY_CONTACT_COPY.slotBodyAlt}>
            <Picture
              className={s.slot_sign_bg}
              alt=""
              src={getPublishingImage('events/ff-today/slot_sign_bg.png')}
              mobileSrc={getPublishingImage('events/ff-today/slot_sign_bg_mo.png')}
            />
            <picture key={spinId} className={s.slot_sign}>
              <source
                media="(max-width: 1024px)"
                srcSet={getPublishingImage(offer.signImageMo)}
              />
              <img src={getPublishingImage(offer.signImage)} alt={offer.signAlt} />
            </picture>
            <div className={s.slot_machine_inner}>
              <div className={s.slot_time}>
                <IconThunder />
                <span key={spinId}>{TODAY_CONTACT_COPY.updatedHoursAgo(offer.updatedHoursAgo)}</span>
                <IconThunder />
              </div>

              <TodayPriceSlot
                priceManwon={offer.priceManwon}
                spinKey={`${offer.id}-${spinId}`}
              />
              <TodayCountdown countdown={countdown} text={TODAY_CONTACT_COPY.countdownReset} />
              
            </div>
          </div>
        </div>
        <StepForm
          form={form}
          intro={
            <>
              <p>
                {TODAY_CONTACT_COPY.stepIntroMain}
                <br />
                {TODAY_CONTACT_COPY.stepIntroSubLine}
              </p>
              <span>{TODAY_CONTACT_COPY.stepIntroHint}</span>
            </>
          }
          step2={{
            formName: LEAD_FORM_NAME.PROMOTION,
            submitLabel: TODAY_CONTACT_COPY.stepSubmitLabel,
            agreement: { marketing: 'required' },
            onSuccess: onLeadSuccess,
          }}
          step2Footer={
            <p className={s.form_desc}>
              {TODAY_CONTACT_COPY.stepFooterLines[0]}
              <br />
              {TODAY_CONTACT_COPY.stepFooterLines[1]}
            </p>
          }
          className={s.lead_form}
        />
      </CommonInner>
    </div>
  );
}
